import { get } from 'svelte/store';

import type { SettingsConfig } from 'src/types/Settings';

import { app, os } from 'src/stores/session/native-bridge';

import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
import { isShellyMobileApp } from 'src/helpers/settings/settings_config_helpers';

// Shared components
import AppVersion from 'src/components/Settings/AppSettings/AppVersion.svelte';

// Legacy components
import BackgroundScan from 'src/components/Settings/AppSettings/BackgroundScan.svelte';
import CarPlay from 'src/components/Settings/AppSettings/CarPlay/CarPlay.svelte';
import LocalControl from 'src/components/Settings/AppSettings/LocalControl.svelte';
import Wearables from 'src/components/Settings/AppSettings/Wearables.svelte';

// V2 components
import BackgroundScanV2 from 'src/components/Settings/AppSettings/BackgroundScanV2.svelte';
import CarPlayV2 from 'src/components/Settings/AppSettings/CarPlay/CarPlayV2.svelte';
import LocalControlV2 from 'src/components/Settings/AppSettings/LocalControlV2.svelte';
import WearablesV2 from 'src/components/Settings/AppSettings/WearablesV2.svelte';

const isNative = (): boolean => get(app) === true;
const isIos = (): boolean => get(os) === 'IOS';
const isNonIosNative = (): boolean => isNative() && !isIos();
const isBackgroundScanSettingVisible = (): boolean => !isActiveHomeShared();
const isCarPlaySettingVisible = (): boolean => isIos() && !isActiveHomeShared();

export const appSettingsConfig: SettingsConfig = {
	legacy: [
		{
			id: 'appVersion',
			title: 'title_app_version',
			titleDefault: 'App version',
			visible: isShellyMobileApp,
			component: AppVersion,
		},
		{
			id: 'connectivity',
			title: 'app_settings_connectivity_title',
			titleDefault: 'Connectivity',
			visible: isShellyMobileApp,
			groups: [
				{
					id: 'localControl',
					title: 'app_settings_local_wifi_control_title',
					titleDefault: 'Local Wifi Control',
					component: LocalControl,
				},
				{
					id: 'backgroundScan',
					title: 'app_settings_background_scan_title',
					titleDefault: 'Background scan',
					visible: isBackgroundScanSettingVisible,
					component: BackgroundScan,
				},
			],
		},
		{
			id: 'wearables',
			title: 'title_wearables',
			titleDefault: 'Wearables',
			visible: isNonIosNative,
			component: Wearables,
		},
		{
			id: 'carPlay',
			title: 'title_car_play_devices',
			titleDefault: 'CarPlay devices',
			visible: isCarPlaySettingVisible,
			component: CarPlay,
		},
	],
	v2: [
		{
			id: 'appVersion',
			title: 'title_app_version',
			titleDefault: 'App version',
			visible: isShellyMobileApp,
			groups: [
				{
					id: 'appVersionGroup',
					components: [{ id: 'appVersion', component: AppVersion }],
				},
			],
		},
		{
			id: 'connectivity',
			title: 'app_settings_connectivity_title',
			titleDefault: 'Connectivity',
			subtitle: 'app_settings_connectivity_subtitle',
			subtitleDefault: 'Configure local network and background scanning settings',
			visible: isShellyMobileApp,
			groups: [
				{
					id: 'connectivityGroup',
					components: [
						{ id: 'localControl', component: LocalControlV2 },
						{
							id: 'backgroundScan',
							component: BackgroundScanV2,
							visible: isBackgroundScanSettingVisible,
						},
					],
				},
			],
		},
		{
			id: 'wearables',
			title: 'title_wearables',
			titleDefault: 'Wearables',
			visible: isNonIosNative,
			component: WearablesV2,
		},
		{
			id: 'carPlay',
			title: 'title_car_play_devices',
			titleDefault: 'CarPlay devices',
			subtitle: 'app_settings_car_play_description',
			subtitleDefault: 'Configure which devices and actions are available in CarPlay.',
			visible: isCarPlaySettingVisible,
			component: CarPlayV2,
		},
	],
};
